import React from "../_snowpack/pkg/react.js";
import createStore from "../common/store.js";
import {
  debounceAsync,
  getQuantityOptions,
  parseQuery,
  replaceSearchQuery
} from "../common/utils.js";
import {getUrl} from "../common/universal.js";
import {Routes} from "../typings/routes.js";
import {useAsync} from "../common/hooks.js";
import request from "../common/request.js";
const store = createStore();
export const useProductStore = store.useStore;
export function ProductStoreProvider(props) {
  const {
    product,
    article: initialArticle,
    quantity: initialQuantity,
    config: initialConfig,
    editorConfig,
    cartItem,
    options = [],
    children
  } = props;
  const {articles} = product;
  const article = initialArticle || articles[0];
  const {min, max, step} = getQuantityOptions(article);
  const quantity = initialQuantity || min;
  const config = initialConfig ?? getInitialConfigValues(props);
  const outsidePrintTemplateFile = getInitialFile(cartItem, "outside");
  const insidePrintTemplateFile = getInitialFile(cartItem, "inside");
  const state = {
    product,
    article,
    quantity,
    min,
    max,
    step,
    config,
    options,
    cartItem,
    outsidePrintTemplateFile,
    insidePrintTemplateFile,
    editorConfig,
    priceResponse: {},
    optionsResponse: {}
  };
  function computedState(s) {
    const {article: article2, quantity: quantity2, product: product2, config: config2, options: options2} = s;
    const slug = product2.page.slug;
    const {min: min2, max: max2, step: step2} = getQuantityOptions(article2);
    const q = quantityFloor(quantity2, min2, max2, step2);
    const configResults = useAsync(() => debouncedGetConfig({
      slug,
      article: article2,
      quantity: q,
      config: config2,
      options: options2
    }), [slug, article2, q, config2, options2]);
    const {value, error} = configResults;
    if (error) {
      throw new Error(error.message);
    }
    const {
      priceResponse,
      optionsResponse,
      editorConfig: updatedEditorConfig
    } = value || {priceResponse: {}, optionsResponse: {}, editorConfig};
    const cleanedConfig = optionsResponse.value?.configValues ?? config2;
    const state2 = {
      ...s,
      config: cleanedConfig,
      min: min2,
      max: max2,
      step: step2,
      quantity: q,
      priceResponse,
      optionsResponse,
      editorConfig: updatedEditorConfig
    };
    setLocationQuery(state2);
    return state2;
  }
  return /* @__PURE__ */ React.createElement(store.Provider, {
    initialState: state,
    computedState
  }, children);
}
function getInitialConfigValues(p) {
  const {cartItem, product} = p;
  if (cartItem) {
    return {};
  }
  const [pageProduct] = product.page.content.product;
  if (pageProduct.component === "standard_product" || pageProduct.component === "model_product") {
    return {};
  }
  const {config} = pageProduct;
  return getConfigValues(config);
}
function getConfigValues(config) {
  return config.reduce((acc, c) => {
    if (c.component === "options_config") {
      const {key} = c;
      const defaultOption = c.options.find((o) => o.default) ?? c.options[0];
      const {value} = defaultOption;
      return Object.assign(acc, {[key]: value});
    }
    if (c.component === "group_config") {
      return Object.assign(acc, getConfigValues(c.options));
    }
    const dims = c.dimensions.reduce((acc2, d) => Object.assign(acc2, {
      [d.key]: parseInt(d.default_value, 10)
    }), {});
    return Object.assign(acc, dims);
  }, {});
}
function getInitialFile(cartItem, type) {
  const search = cartItem || parseQuery();
  const name = search[`${type}_print_template_name`];
  const path = search[`${type}_print_template_path`];
  if (!name || !path)
    return;
  const url = getUrl(Routes.getPrintTemplate, {path});
  const thumbnailUrl = getUrl(Routes.getPrintThumbnail, {path});
  const [, objectName] = path.split("/");
  return {
    url,
    thumbnailUrl,
    name,
    path,
    type: "application/pdf",
    objectName
  };
}
function quantityFloor(quantity, min, max, step) {
  const rest = quantity % step;
  const steppedQ = quantity - rest;
  return Math.max(min, Math.min(steppedQ, max));
}
const debouncedGetConfig = debounceAsync(getConfig, 200);
async function getConfig(args) {
  const {slug, article, quantity, config, options} = args;
  const url = getUrl(Routes.productConfig, {slug});
  return request(url, {
    method: "post",
    body: {
      article_id: article.id,
      option_ids: options.map((o) => o.id),
      quantity,
      config
    }
  });
}
function setLocationQuery(s) {
  const {
    quantity,
    config: product_config,
    article,
    options,
    outsidePrintTemplateFile,
    insidePrintTemplateFile
  } = s;
  const query = {
    ...parseQuery(),
    quantity,
    product_config,
    article_id: article?.id,
    option_ids: options?.map(({id}) => id),
    outside_print_template_name: outsidePrintTemplateFile?.name,
    outside_print_template_path: outsidePrintTemplateFile?.path,
    inside_print_template_name: insidePrintTemplateFile?.name,
    inside_print_template_path: insidePrintTemplateFile?.path
  };
  replaceSearchQuery(query);
}
